import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from 'leaflet';
import { getOrDeleteRequest } from "../../utils/utils";
import SelectMultiple from "../SelectMultiple";
import { usePanel } from "../PanelContext/PanelContext";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export default function Map() {
    const { isPanelVisible } = usePanel();
    const [markers, setMarkers] = useState([]);
    const [filters, setFilters] = useState({
        state: '',
        city: '',
        district: ''
    });
    const [filterOptions, setFilterOptions] = useState({
        states: [],
        cities: [],
        districts: []
    });
    const [loading, setLoading] = useState({
        states: false,
        cities: false,
        districts: false
    });

    const getStates = async () => {
        try {
            setLoading(prev => ({ ...prev, states: true }));
            const response = await getOrDeleteRequest('states/filter', 'GET');
            setFilterOptions(prev => ({
                ...prev,
                states: response.data || []
            }));
        } catch (error) {
            console.error("Error fetching states", error);
        } finally {
            setLoading(prev => ({ ...prev, states: false }));
        }
    };

    const getCities = async (stateId) => {
        if (!stateId) {
            setFilterOptions(prev => ({ ...prev, cities: [], districts: [] }));
            setFilters(prev => ({ ...prev, city: '', district: '' }));
            return;
        }

        try {
            setLoading(prev => ({ ...prev, cities: true }));
            const response = await getOrDeleteRequest(`cities/by-state/${stateId}`, 'GET');
            // console.log(response.data, "response.data cities");
            setFilterOptions(prev => ({
                ...prev,
                cities: response.data || [],
                districts: []
            }));
            setFilters(prev => ({ ...prev, city: '', district: '' }));
        } catch (error) {
            console.error("Error fetching cities", error);
        } finally {
            setLoading(prev => ({ ...prev, cities: false }));
        }
    };

    const getDistricts = async (cityId) => {
        if (!cityId) {
            setFilterOptions(prev => ({ ...prev, districts: [] }));
            setFilters(prev => ({ ...prev, district: '' }));
            return;
        }

        try {
            setLoading(prev => ({ ...prev, districts: true }));
            const response = await getOrDeleteRequest(`districts/by-city/${cityId}`, 'GET');
            setFilterOptions(prev => ({
                ...prev,
                districts: response.data || []
            }));
            setFilters(prev => ({ ...prev, district: '' }));
        } catch (error) {
            console.error("Error fetching districts", error);
        } finally {
            setLoading(prev => ({ ...prev, districts: false }));
        }
    };

    const getUbications = async () => {
        try {
            let url = 'dashboard/companies-map-data';
            const params = new URLSearchParams();

            if (filters.state) params.append('state_id', filters.state);
            if (filters.city) params.append('city_id', filters.city);
            if (filters.district) params.append('district_id', filters.district);

            if (params.toString()) url += `?${params.toString()}`;

            const response = await getOrDeleteRequest(url, 'GET');

            setMarkers(response.data.length > 0 ? response.data.map((organization) => ({
                id: organization.id,
                position: [organization.latitude, organization.longitude],
                company_name: organization.company_name,
            })).filter((marker) => marker.position[0] !== null && marker.position[1] !== null)
                : []);
        } catch (error) {
            console.error("Error fetching properties", error);
        }
    };

    const handleFilterChange = async (value, name) => {
        !value && setFilters({
            state: '',
            city: '',
            district: ''
        });
        if (!value) return;

        setFilters(prev => {
            const newFilters = { ...prev, [name]: value };

            if (name === 'state') {
                newFilters.city = '';
                newFilters.district = '';
            } else if (name === 'city') {
                newFilters.district = '';
            }

            return newFilters;
        });

        if (name === 'state') {
            // console.log(value, "value state");
            await getCities(value);
        } else if (name === 'city') {
            // console.log(value, "value city");
            await getDistricts(value);
        }
    };

    const applyFilters = () => {
        getUbications();
    };

    const resetFilters = () => {
        setFilters({
            state: '',
            city: '',
            district: ''
        });
        setFilterOptions(prev => ({
            ...prev,
            cities: [],
            districts: []
        }));
        getUbications();
    };

    useEffect(() => {
        getStates();
        getUbications();
    }, []);

    return (
        <div className="h-[500px] w-full flex flex-col">
            <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-t-lg">
                <div className="flex-1 min-w-[200px]">
                    <SelectMultiple
                        name="state"
                        label={"Departamento"}
                        array={filterOptions.states}
                        value={filters.state}
                        onChange={(e) => handleFilterChange(e, "state")}
                        disabled={loading.states}
                    />
                </div>

                <div className="flex-1 min-w-[200px]">
                    <SelectMultiple
                        name="city"
                        label={"Municipio"}
                        array={filterOptions.cities}
                        value={filters.city}
                        onChange={(e) => handleFilterChange(e, "city")}
                        disabled={!filters.state || loading.cities}
                    />
                </div>

                <div className="flex-1 min-w-[200px]">
                    <SelectMultiple
                        name="district"
                        label="Distrito"
                        array={filterOptions.districts}
                        value={filters.district}
                        onChange={(e) => handleFilterChange(e, "district")}
                        disabled={!filters.city || loading.districts}
                    />
                </div>

                <div className="flex items-end gap-2">
                    <button
                        onClick={applyFilters}
                        className="px-4 py-2.5 bg-turquesa text-white rounded-md hover:opacity-90"
                    >
                        Aplicar
                    </button>
                    <button
                        onClick={resetFilters}
                        className="px-4 py-2.5 bg-gray-500 text-white rounded-md hover:opacity-90"
                    >
                        Limpiar
                    </button>
                </div>
            </div>

            <div className="h-full w-full">
                {isPanelVisible && <MapContainer
                    center={[13.6984, -89.1914]}
                    zoom={8}
                    className="h-full w-full"
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {markers.map((marker) => (
                        <Marker key={marker.id} position={marker.position}>
                            <Popup>
                                <>{marker.company_name}</>
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>}

                {!isPanelVisible && <MapContainer
                    center={[13.6984, -89.1914]}
                    zoom={8}
                    className="h-full w-full"
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {markers.map((marker) => (
                        <Marker key={marker.id} position={marker.position}>
                            <Popup>
                                <>{marker.company_name}</>
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>}
            </div>
        </div>
    );
}